import {
    Camera,
    Color,
    DoubleSide,
    Intersection,
    Mesh,
    MeshBasicMaterial,
    Object3D,
    PlaneGeometry,
    Quaternion,
    Raycaster,
    Scene,
    SphereBufferGeometry,
    TorusGeometry,
    Vector3,
} from "three";
import { AssetType } from "./enums";
import { builder } from "./global";
import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";

export class TransformHelper extends Object3D {
    radius!: number;
    xRotation: Mesh;
    yRotation: Mesh;
    zRotation: Mesh;
    selectedObject: StaticMesh | Robot | undefined;
    targetMeshes: Mesh[] = [];
    private circleGeometry: TorusGeometry;
    numberOfPoints = 32;
    plane: TransformPlane;
    domRef: HTMLDivElement;
    private mouseDown = false;
    private _dragging = false;
    private raycaster: Raycaster;
    camera: Camera;
    private angleSnap = Math.PI / 4;
    private currentSelectedRing: Mesh | undefined = undefined;
    private axis: string | undefined;
    private pointStart = new Vector3();
    private pointEnd = new Vector3();
    private offset = new Vector3();
    private worldPositionStart = new Vector3();
    private quaternionStart = new Quaternion();
    private worldPosition = new Vector3();
    private tempVector = new Vector3();
    get dragging(): boolean {
        return this._dragging;
    }
    set dragging(value: boolean) {
        this._dragging = value;
        this.dispatchEvent({ type: "dragging-change", value: value });
        this.dispatchEvent({ type: "change" });
    }
    constructor(ref: HTMLDivElement, camera: Camera) {
        super();
        this.visible = false;
        this.circleGeometry = new TorusGeometry();
        this.setRadius(0.2);
        this.domRef = ref;
        this.camera = camera;
        this.raycaster = new Raycaster();
        this.xRotation = new Mesh(
            this.circleGeometry,
            new MeshBasicMaterial({ color: "#FF0000", wireframe: false })
        );
        this.xRotation.rotation.y = Math.PI / 2;
        this.xRotation.name = "X";
        this.yRotation = new Mesh(this.circleGeometry, new MeshBasicMaterial({ color: "#00FF00" }));
        this.yRotation.rotation.x = Math.PI / 2;
        this.yRotation.name = "Y";
        this.zRotation = new Mesh(this.circleGeometry, new MeshBasicMaterial({ color: "#0000FF" }));
        this.zRotation.name = "Z";
        this.plane = new TransformPlane();
        this.add(this.xRotation);
        this.add(this.yRotation);
        this.add(this.zRotation);
        this.add(this.plane);
        this.dragging = false;
        this.domRef.addEventListener("pointerdown", this.pointerDown.bind(this));
        this.domRef.addEventListener("pointermove", this.pointerHover.bind(this));
        this.domRef.addEventListener("pointermove", this.pointerMove.bind(this));
        this.domRef.addEventListener("pointerup", this.pointerUp.bind(this));
    }

    setRadius(r: number): void {
        this.radius = r;
        this.circleGeometry.copy(this.CircleGeometry(1));
    }
    addToScene(scene: Scene): void {
        scene.add(this);
    }
    setPosition(pos: Vector3): void {
        this.position.copy(pos);
    }
    private rotate(alpha: number): void {
        if (this.selectedObject) {
            switch (this.selectedObject.type) {
                case AssetType.Robot:
                    this.selectedObject.rotate(alpha);
                    break;
                case AssetType.Console:
                case AssetType.Stack:
                case AssetType.Trocar:
                case AssetType.Trolley:
                case AssetType.Anesthesia:
                case AssetType.Surgeon:
                case AssetType.Slave:
                    this.selectedObject.rotate(alpha);
            }
        }
    }
    getPointer(event: PointerEvent): { x: number; y: number; button: number } {
        const rect = this.domRef.getBoundingClientRect();
        return {
            x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
            y: (-(event.clientY - rect.top) / rect.height) * 2 + 1,
            button: event.button,
        };
    }
    pointerDown(ev: PointerEvent): void {
        if (!this.domRef) return;
        if (this.dragging) return;
        if (!this.selectedObject) return;
        if (!this.axis) return;
        const pointer = this.getPointer(ev);
        this.raycaster.setFromCamera(pointer, this.camera);
        const planeIntersect = intersectObjectWithRay(this.plane, this.raycaster, true);
        this.quaternionStart = new Quaternion();
        if (planeIntersect) {
            const worldPositionStart = this.selectedObject.getPosition();
            this.selectedObject.group.matrixWorld.decompose(
                worldPositionStart,
                this.quaternionStart,
                new Vector3()
            );
            this.pointStart.copy(planeIntersect.point).sub(worldPositionStart);
        }
        this.dragging = true;
        this.dispatchEvent({ type: "mousedown" });
    }
    pointerHover(ev: PointerEvent): void {
        if (!this.domRef) return;
        if (this.dragging) return;
        const pointer = this.getPointer(ev);
        this.raycaster.setFromCamera(pointer, this.camera);
        const intersects = this.raycaster.intersectObjects(this.children, true);

        if (this.currentSelectedRing) {
            const Mat = this.currentSelectedRing.material as MeshBasicMaterial;
            Mat.color = Mat.userData["axisColor"];
        }
        if (intersects.length > 0) {
            this.currentSelectedRing = intersects[0].object as Mesh;
            this.axis = this.currentSelectedRing.name;
            const Mat = this.currentSelectedRing.material as MeshBasicMaterial;
            Mat.userData["axisColor"] = Mat.color;
            Mat.color = new Color("#FFFF00");
        }
    }
    pointerMove(ev: PointerEvent): void {
        if (!this.domRef) return;

        if (!this.dragging) return;
        if (!this.selectedObject) return;

        if (this.axis) {
            const pointer = this.getPointer(ev);
            this.raycaster.setFromCamera(pointer, this.camera);
            const planeIntersect = intersectObjectWithRay(this.plane, this.raycaster, true);
            if (!planeIntersect) return;
            this.pointEnd.copy(planeIntersect.point).sub(this.worldPositionStart);
            this.offset.copy(this.pointEnd).sub(this.pointStart);
            const worldPosition = this.selectedObject.getPosition();
            this.tempVector.setFromMatrixPosition(this.camera.matrixWorld);
            const ROTATION_SPEED = 80 / worldPosition.distanceTo(this.tempVector);
            let unitVector = new Vector3();
            const rotationVector = new Vector3();
            switch (this.axis) {
                case "X":
                    unitVector = new Vector3(1, 0, 0);
                    break;
                case "Y":
                    unitVector = new Vector3(0, 1, 0);
                    break;
                case "Z":
                    unitVector = new Vector3(0, 0, 1);
                    break;
            }
            rotationVector.copy(unitVector);
            this.tempVector.copy(unitVector);
            const q = new Quaternion();
            const pos = new Vector3();
            const scale = new Vector3();
            this.selectedObject.group.updateMatrixWorld(true);
            this.selectedObject.group.matrixWorld.decompose(pos, q, scale);
            this.tempVector.applyQuaternion(q);
            this.camera.updateMatrix();
            const cameraPos = new Vector3();
            const cameraQ = new Quaternion();
            const cameraS = new Vector3();
            const eye = new Vector3();
            this.camera.matrixWorld.decompose(cameraPos, cameraQ, cameraS);
            eye.copy(cameraPos).sub(worldPosition).normalize();
            let rotationAngle = this.offset.dot(this.tempVector.cross(eye).normalize()); // * ROTATION_SPEED
            if (this.selectedObject.type == AssetType.Robot) {
                rotationAngle *= 0.5;
                rotationAngle = Math.round(rotationAngle / this.angleSnap) * this.angleSnap;
                rotationAngle += (this.selectedObject as Robot).getTransform().rotation;
                (this.selectedObject as Robot).setRootRotation(rotationAngle, builder.room);
            } else {
                rotationAngle *= ROTATION_SPEED;
                rotationAngle = Math.round(rotationAngle / this.angleSnap) * this.angleSnap;
                const tempQ = new Quaternion();
                tempQ.setFromAxisAngle(rotationVector, rotationAngle);
                this.selectedObject.group.quaternion.copy(this.quaternionStart);
                this.selectedObject.group.quaternion.multiply(tempQ).normalize();
            }
        }
    }
    pointerUp(): void {
        if (!this.domRef) return;
        if (this.dragging) {
            this.update();
            this.dragging = false;
            this.axis = undefined;
        }
        this.dispatchEvent({ type: "mouseup" });
    }
    clearSelection(): void {
        this.selectedObject = undefined;
        this.visible = false;
        this.axis = undefined;
    }
    update(): void {
        const q = new Quaternion();
        const pos = new Vector3();
        const scale = new Vector3();
        if (this.selectedObject) {
            this.selectedObject.group.updateMatrixWorld(true);
            this.selectedObject.group.matrixWorld.decompose(pos, q, scale);
            this.setPosition(this.selectedObject.getPosition());
            this.worldPosition.copy(pos);
            this.quaternion.copy(q);
            this.setPosition(pos);
        }
    }
    setSelectedObject(
        obj: StaticMesh | Robot,
        targetMeshes: Mesh[],
        setting: { mode: string; axis: string } = { mode: "local", axis: "Y" }
    ): void {
        this.selectedObject = obj;
        this.targetMeshes = targetMeshes;
        this.visible = true;
        const q = new Quaternion();
        const pos = new Vector3();
        const scale = new Vector3();
        this.selectedObject.group.updateMatrixWorld(true);
        this.selectedObject.group.matrixWorld.decompose(pos, q, scale);
        this.setPosition(this.selectedObject.getPosition());
        const size = this.selectedObject.getDimension();
        const r = Math.max(size.width, size.depth);
        this.setRadius(r);
        switch (setting.mode) {
            case "local":
                this.quaternion.copy(q);
                break;
            case "world":
                this.quaternion.copy(new Quaternion());
                break;
        }

        switch (setting.axis) {
            case "Y":
                this.xRotation.visible = false;
                this.zRotation.visible = false;
                this.yRotation.visible = true;

                break;
            case "XZ":
                this.xRotation.visible = true;
                this.zRotation.visible = true;
                this.yRotation.visible = false;
                break;
            case "XY":
                this.xRotation.visible = true;
                this.zRotation.visible = false;
                this.yRotation.visible = true;
                break;
            case "XYZ":
                this.xRotation.visible = true;
                this.zRotation.visible = true;
                this.yRotation.visible = true;
                break;
        }
    }
    getMeshes(): Mesh[] {
        return [this.xRotation, this.yRotation, this.zRotation];
    }
    private CircleGeometry(arc: number) {
        const geometry = new TorusGeometry(this.radius, 0.005, 4, 32, arc * Math.PI * 2);
        return geometry;
    }
}
export class SphereHelper extends Mesh {
    constructor() {
        const geom = new SphereBufferGeometry(0, 32, 32);
        const mat = new MeshBasicMaterial({
            color: "#19ddf7",
            visible: false,
            wireframe: false,
            transparent: true,
            opacity: 0.1,
            toneMapped: false,
        });
        super(geom, mat);
    }
    updateRadius(r: number): void {
        if (r === NaN) {
            return;
        }
        if (r === 0) (this.material as MeshBasicMaterial).visible = false;
        else (this.material as MeshBasicMaterial).visible = true;
        const geom = new SphereBufferGeometry(r, 32, 32);
        this.geometry.copy(geom);
    }
    findRadiusAndCenter(
        p1: Vector3,
        p2: Vector3,
        p3: Vector3
    ): { radius: number; center: Vector3 } {
        const v = new Vector3().copy(p3);
        const p1a = new Vector3().copy(p1);
        v.sub(p2);
        v.normalize();
        p1a.sub(p2);
        const c = p1a.projectOnVector(v);
        //c.multiply(v);
        c.add(p2);

        const r = p1.distanceTo(c);
        this.updateRadius(r);
        this.position.copy(c);
        return { radius: r, center: c };
    }
}
class TransformPlane extends Mesh {
    constructor() {
        super(
            new PlaneGeometry(100000, 100000, 2, 2),
            new MeshBasicMaterial({
                visible: false,
                wireframe: true,
                side: DoubleSide,
                transparent: true,
                opacity: 0.1,
                toneMapped: false,
            })
        );
        this.type = "TransformPlane";
    }
}
function intersectObjectWithRay(
    object: Object3D,
    raycaster: Raycaster,
    includeInvisible: boolean
): undefined | Intersection {
    const allIntersections = raycaster.intersectObject(object, true);

    for (let i = 0; i < allIntersections.length; i++) {
        if (allIntersections[i].object.visible || includeInvisible) {
            return allIntersections[i];
        }
    }
    return undefined;
}
